// Canonical joint model for CADtomie Gait.
//
// We consume MediaPipe BlazePose's 33-landmark output but expose a *named*,
// stable joint model so the analytical core never depends on raw indices.
// If we later swap the pose engine (e.g. native Apple Vision in Phase 3), only
// the adapter that produces `Landmark[]` changes — this model stays fixed.

/** A single 2D/3D landmark in normalized image space (x,y in [0,1]). */
export interface Landmark {
  x: number;
  y: number;
  z: number;
  /** Detector visibility/presence in [0,1]; lower = less trustworthy. */
  visibility: number;
}

/** One frame of pose: the 33 BlazePose landmarks + a true timestamp. */
export interface PoseFrame {
  tMs: number;
  /** Image-space landmarks (normalized 0..1). */
  landmarks: Landmark[];
  /** Optional metric-ish "world" landmarks (meters, origin at hip center). */
  world?: Landmark[];
}

/** BlazePose 33-point indices (the subset we use). */
export const BP = {
  NOSE: 0,
  LEFT_EAR: 7,
  RIGHT_EAR: 8,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
  LEFT_HEEL: 29,
  RIGHT_HEEL: 30,
  LEFT_FOOT_INDEX: 31,
  RIGHT_FOOT_INDEX: 32,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
} as const;

export type Side = "L" | "R";

/** Named accessor over a PoseFrame. Keeps the rest of the code index-free. */
export class Joints {
  constructor(private readonly f: PoseFrame) {}

  private at(i: number): Landmark {
    return this.f.landmarks[i];
  }

  nose() {
    return this.at(BP.NOSE);
  }
  shoulder(s: Side) {
    return this.at(s === "L" ? BP.LEFT_SHOULDER : BP.RIGHT_SHOULDER);
  }
  hip(s: Side) {
    return this.at(s === "L" ? BP.LEFT_HIP : BP.RIGHT_HIP);
  }
  knee(s: Side) {
    return this.at(s === "L" ? BP.LEFT_KNEE : BP.RIGHT_KNEE);
  }
  ankle(s: Side) {
    return this.at(s === "L" ? BP.LEFT_ANKLE : BP.RIGHT_ANKLE);
  }
  heel(s: Side) {
    return this.at(s === "L" ? BP.LEFT_HEEL : BP.RIGHT_HEEL);
  }
  footIndex(s: Side) {
    return this.at(s === "L" ? BP.LEFT_FOOT_INDEX : BP.RIGHT_FOOT_INDEX);
  }
  elbow(s: Side) {
    return this.at(s === "L" ? BP.LEFT_ELBOW : BP.RIGHT_ELBOW);
  }
  wrist(s: Side) {
    return this.at(s === "L" ? BP.LEFT_WRIST : BP.RIGHT_WRIST);
  }

  /** Derived pelvis center (mid-hip). */
  pelvisCenter(): Landmark {
    return midpoint(this.hip("L"), this.hip("R"));
  }
  /** Derived shoulder center. */
  shoulderCenter(): Landmark {
    return midpoint(this.shoulder("L"), this.shoulder("R"));
  }
}

/**
 * Return the frame in *isotropic* image space. MediaPipe normalizes x by frame
 * WIDTH and y by frame HEIGHT separately, so raw normalized coordinates are
 * anisotropic: a given x-distance and y-distance are not on the same scale.
 * That distorts every angle (which mixes x and y) and every x/y-combining
 * distance, and — worse for us — makes the distortion depend on the video's
 * aspect ratio, so the same patient filmed in 16:9 vs 9:16 yields different
 * "angles". Multiplying x by the aspect ratio (width / height) restores
 * square-pixel space.
 *
 * `aspect = 1` is a no-op (returns the same frame reference) and reproduces the
 * pre-isotropic behaviour exactly — callers that don't pass an aspect are
 * unchanged.
 */
export function isotropicFrame(f: PoseFrame, aspect: number): PoseFrame {
  if (!aspect || aspect === 1) return f;
  return {
    ...f,
    landmarks: f.landmarks.map((l) => ({ ...l, x: l.x * aspect })),
  };
}

export function midpoint(a: Landmark, b: Landmark): Landmark {
  return {
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
    z: (a.z + b.z) / 2,
    visibility: Math.min(a.visibility, b.visibility),
  };
}

/** Euclidean distance in image space (x,y only — what we render/measure on). */
export function dist2D(a: Landmark, b: Landmark): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * Torso length = shoulder-center to pelvis-center distance. This is our scale
 * normalizer: dividing pixel/normalized excursions by torso length makes every
 * feature camera-distance invariant, which is what makes longitudinal
 * comparison across sessions valid.
 */
export function torsoLength(f: PoseFrame): number {
  const j = new Joints(f);
  return dist2D(j.shoulderCenter(), j.pelvisCenter());
}

/** Skeleton edges (named pairs) for overlay rendering. */
export const SKELETON_EDGES: Array<[number, number]> = [
  [BP.LEFT_SHOULDER, BP.RIGHT_SHOULDER],
  [BP.LEFT_SHOULDER, BP.LEFT_HIP],
  [BP.RIGHT_SHOULDER, BP.RIGHT_HIP],
  [BP.LEFT_HIP, BP.RIGHT_HIP],
  [BP.LEFT_HIP, BP.LEFT_KNEE],
  [BP.LEFT_KNEE, BP.LEFT_ANKLE],
  [BP.LEFT_ANKLE, BP.LEFT_HEEL],
  [BP.LEFT_HEEL, BP.LEFT_FOOT_INDEX],
  [BP.LEFT_ANKLE, BP.LEFT_FOOT_INDEX],
  [BP.RIGHT_HIP, BP.RIGHT_KNEE],
  [BP.RIGHT_KNEE, BP.RIGHT_ANKLE],
  [BP.RIGHT_ANKLE, BP.RIGHT_HEEL],
  [BP.RIGHT_HEEL, BP.RIGHT_FOOT_INDEX],
  [BP.RIGHT_ANKLE, BP.RIGHT_FOOT_INDEX],
];
